import { cn } from "@/components/kit";
import { formatLak } from "@/shared/lib/currency";
import { useEventSummaryQuery } from "../api/queries";
import { EVENT_EXPENSE_LABELS } from "../lib/labels";

type EventSaleSummaryPanelProps = {
  eventId: string;
};

export function EventSaleSummaryPanel({ eventId }: EventSaleSummaryPanelProps) {
  const summary = useEventSummaryQuery(eventId, true);
  const data = summary.data;

  if (summary.isLoading || !data) {
    return <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດສະຫຼຸບ...</p>;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="brand-card flex flex-col gap-1 p-5">
        <span className="text-muted-foreground text-xs">ກຳໄລ</span>
        <span className="font-display text-3xl tabular-nums leading-tight">
          {formatLak(data.profit)}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <SummaryCard label="ລາຍຮັບ" value={formatLak(data.revenue)} />
        <SummaryCard label="ລາຍຈ່າຍ" value={formatLak(data.expenseTotal)} />
        <SummaryCard
          className="col-span-2"
          label="ຈຳນວນບິນ"
          value={String(data.saleCount)}
        />
      </div>
      <section className="brand-card flex flex-col gap-3 p-4">
        <h3 className="font-medium text-sm">ລາຍຈ່າຍຕາມໝວດ</h3>
        <ul className="flex flex-col gap-3">
          {data.expensesByCategory.map((row) => (
            <li
              key={row.category}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <span>{EVENT_EXPENSE_LABELS[row.category]}</span>
              <span className="shrink-0 tabular-nums">
                {formatLak(row.amount)}
              </span>
            </li>
          ))}
        </ul>
      </section>
      <section className="flex flex-col gap-2">
        <h3 className="font-medium text-sm">ສິນຄ້າຂາຍດີ</h3>
        {data.topItems.length === 0 ? (
          <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີຍອດຂາຍ</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {data.topItems.map((item) => (
              <li
                key={item.itemId}
                className="brand-card flex flex-col gap-1 p-4"
              >
                <span className="font-medium leading-snug">{item.name}</span>
                <span className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">
                    {item.quantity} ຊິ້ນ
                  </span>
                  <span className="tabular-nums">{formatLak(item.amount)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div
      className={cn("brand-card flex min-w-0 flex-col gap-1 p-4", className)}
    >
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="truncate font-display text-xl tabular-nums">
        {value}
      </span>
    </div>
  );
}
